/**
 * InfinitSales - Saved Messages Service
 * Frontend client for /api/saved-messages CRUD endpoints.
 *
 * Data shape:
 *   {
 *     id, user_id, title, text,
 *     attachments: [{ file_url, file_name, mime_type, type, file_size }],
 *     audio: { file_url, file_name, duration, mime_type, transcript } | null,
 *     created_at, updated_at
 *   }
 */
import api from './api';

const BASE = '/api/saved-messages';

const unwrap = (error, fallbackMsg) => {
  const detail = error?.response?.data?.detail;
  return new Error(
    (typeof detail === 'string' && detail) ||
    detail?.message ||
    error?.message ||
    fallbackMsg
  );
};

const savedMessagesService = {
  /**
   * List all saved messages for the authenticated user (newest first).
   * @returns {Promise<{items: Array, count: number}>}
   */
  async list() {
    try {
      const response = await api.get(`${BASE}/`);
      return response.data || { items: [], count: 0 };
    } catch (error) {
      throw unwrap(error, 'Erro ao carregar mensagens salvas');
    }
  },

  /**
   * Create a saved message.
   * @param {Object} payload - { title, text, attachments, audio }
   */
  async create(payload) {
    try {
      const response = await api.post(`${BASE}/`, payload);
      return response.data;
    } catch (error) {
      throw unwrap(error, 'Erro ao criar mensagem salva');
    }
  },

  /**
   * Update an existing saved message by id.
   */
  async update(id, payload) {
    try {
      const response = await api.put(`${BASE}/${id}`, payload);
      return response.data;
    } catch (error) {
      throw unwrap(error, 'Erro ao atualizar mensagem salva');
    }
  },

  /**
   * Delete a saved message by id.
   */
  async remove(id) {
    try {
      const response = await api.delete(`${BASE}/${id}`);
      return response.data;
    } catch (error) {
      throw unwrap(error, 'Erro ao excluir mensagem salva');
    }
  },
};

export default savedMessagesService;
